import { useMemo } from 'react'

import { GameBackdrop } from '@/features/stadium/GameBackdrop'
import { MatchFocusCard } from '@/features/stadium/MatchFocusCard'
import { StadiumBracket } from '@/features/stadium/StadiumBracket'
import type { Bracket, BracketMatch, RGNEvent } from '@/lib/api/schemas'

/**
 * LIVE phase — bracket on the left, current-match focus card on the
 * right. 40 / 60 horizontal split (the focus card is the "TV show
 * moment"; the bracket is supporting context).
 *
 * The two children share the same `focusMatchId` so the bracket can
 * highlight the position that the focus card is zooming in on.
 *
 * Bracket lives behind a translucent surface so the bracket-line
 * connectors (drawn in the bracket itself) read against the panel
 * background rather than the GameBackdrop, which would compete.
 */
export function LivePhase({
  event,
  bracket,
  recentlyCompleted,
}: {
  event: RGNEvent
  bracket: Bracket | null
  recentlyCompleted?: ReadonlySet<string>
}) {
  const focusId = useMemo(() => pickFocusMatchId(bracket), [bracket])

  return (
    <div className="relative flex h-full gap-[clamp(16px,1.6vw,32px)] px-[clamp(16px,1.6vw,32px)] py-[clamp(12px,1.4vh,20px)]">
      <GameBackdrop event={event} mode="paneled" />

      <section
        // Left pane — bracket. 40% of the body, deepened surface so the
        // connector lines + match cards read against the panel rather
        // than the backdrop image bleeding through.
        className="relative flex h-full min-w-0 basis-[40%] overflow-hidden rounded-2xl border bg-[color-mix(in_oklab,var(--tv-bg-elev)_78%,transparent)]"
        style={{ borderColor: 'var(--tv-border)' }}
        aria-label="Bracket"
      >
        {bracket ? (
          <StadiumBracket
            bracket={bracket}
            focusMatchId={focusId}
            recentlyCompleted={recentlyCompleted}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <p
              className="font-display font-medium uppercase"
              style={{
                color: 'var(--tv-fg-subtle)',
                letterSpacing: '0.3em',
                fontSize: 'clamp(0.85rem, 1vw, 1.05rem)',
              }}
            >
              Bracket loading…
            </p>
          </div>
        )}
      </section>

      <section
        // Right pane — focus card. 60% width, same panel chrome as the
        // bracket for visual cohesion.
        className="relative flex h-full min-w-0 flex-1 overflow-hidden rounded-2xl border bg-[color-mix(in_oklab,var(--tv-bg-elev)_78%,transparent)]"
        style={{ borderColor: 'var(--tv-border)' }}
        aria-label="Current match focus"
      >
        <div className="h-full w-full">
          <MatchFocusCard bracket={bracket} />
        </div>
      </section>
    </div>
  )
}

/** Mirror of MatchFocusCard's internal picker so the bracket can
 *  highlight the same match. Duplicated rather than imported because
 *  the picker is meaningful in its own right at this level (the
 *  composition decides who's in focus, not the card itself). */
function pickFocusMatchId(bracket: Bracket | null): string | null {
  if (!bracket?.matches) return null
  const incomplete = bracket.matches.filter((m) => !isComplete(m))
  if (incomplete.length === 0) return null

  const inProgress = incomplete.find(
    (m) => (m.status ?? '').toUpperCase() === 'IN_PROGRESS',
  )
  if (inProgress) return inProgress.matchId

  const awaiting = incomplete.find(
    (m) => (m.status ?? '').toUpperCase() === 'AWAITING_CONFIRMATION',
  )
  if (awaiting) return awaiting.matchId

  const scheduled = incomplete
    .filter(
      (m) =>
        ((m.status ?? '').toUpperCase() === 'SCHEDULED' ||
          (m.status ?? '').toUpperCase() === 'PROPOSED') &&
        m.participantA &&
        m.participantB,
    )
    .sort((a, b) => (toMs(a.scheduledAt) ?? Infinity) - (toMs(b.scheduledAt) ?? Infinity))
  if (scheduled.length > 0) return scheduled[0].matchId

  const ready = incomplete.find((m) => m.participantA && m.participantB)
  if (ready) return ready.matchId

  return incomplete[0]?.matchId ?? null
}

function isComplete(m: BracketMatch): boolean {
  const s = (m.status ?? '').toUpperCase()
  return s === 'COMPLETE' || s === 'COMPLETED' || s === 'OVERRIDDEN'
}

function toMs(v: number | string | null | undefined): number | null {
  if (v == null) return null
  if (typeof v === 'number') return v
  const parsed = Date.parse(v)
  return Number.isFinite(parsed) ? parsed : null
}
